import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  AlertCircle, 
  CheckCircle, 
  Info, 
  ArrowLeft, 
  Download, 
  Phone,
  Calendar,
  Lightbulb,
  Activity,
  Target,
  Clock
} from 'lucide-react';

/**
 * ADHDScreening Component
 * 
 * Based on GitHub patterns from:
 * - Healthcare React applications (OpenMRS, Medplum)
 * - Mental health screening tools (MindStrong, Talkspace)
 * - Responsive design patterns from Material-UI and Tailwind
 * 
 * Features:
 * - Adult ADHD Self-Report Scale (ASRS-v1.1) Screener
 * - Tablet-optimized responsive layout (768px - 1024px)
 * - Progress tracking and results interpretation
 * - Accessibility features (ARIA labels, keyboard navigation)
 * - Integration with Parivarthan routing structure from App.js
 * 
 * Reference: Based on WHO Adult ADHD Self-Report Scale
 * 
 * @component
 * @returns {React.Component} ADHD screening assessment tool
 */

const ADHDScreening = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  // Adult ADHD Self-Report Scale (ASRS-v1.1) Screener - Part A (6 questions)
  // This is the validated screener version
  const questions = [
    {
      id: 'q1',
      text: 'How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done?',
      category: 'inattention',
      scoringThreshold: 2 // Scores 2-4 are considered symptomatic
    },
    {
      id: 'q2',
      text: 'How often do you have difficulty getting things in order when you have to do a task that requires organization?',
      category: 'inattention',
      scoringThreshold: 2
    },
    {
      id: 'q3',
      text: 'How often do you have problems remembering appointments or obligations?',
      category: 'inattention',
      scoringThreshold: 2
    },
    {
      id: 'q4',
      text: 'When you have a task that requires a lot of thought, how often do you avoid or delay getting started?',
      category: 'inattention',
      scoringThreshold: 2
    },
    {
      id: 'q5',
      text: 'How often do you fidget or squirm with your hands or feet when you have to sit down for a long time?',
      category: 'hyperactivity',
      scoringThreshold: 3 // For this question, 3-4 are considered symptomatic
    },
    {
      id: 'q6',
      text: 'How often do you feel overly active and compelled to do things, like you were driven by a motor?',
      category: 'hyperactivity',
      scoringThreshold: 3
    }
  ];

  // Answer options for frequency scale
  const answerOptions = [
    { value: 0, label: 'Never', color: 'hover:bg-green-50' },
    { value: 1, label: 'Rarely', color: 'hover:bg-blue-50' },
    { value: 2, label: 'Sometimes', color: 'hover:bg-yellow-50' },
    { value: 3, label: 'Often', color: 'hover:bg-orange-50' },
    { value: 4, label: 'Very Often', color: 'hover:bg-red-50' }
  ];

  // Calculate ASRS score based on threshold method
  const calculateScore = () => {
    let symptomaticCount = 0;
    
    questions.forEach(question => {
      const answer = answers[question.id];
      if (answer >= question.scoringThreshold) {
        symptomaticCount++;
      }
    });
    
    return symptomaticCount;
  };

  // Interpret results
  const getInterpretation = (symptomaticScore) => {
    if (symptomaticScore >= 4) {
      return {
        level: 'High Likelihood',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: AlertCircle,
        description: 'Your responses suggest symptoms consistent with ADHD.',
        recommendation: 'We strongly recommend scheduling a comprehensive evaluation with a mental health professional or psychiatrist who specializes in ADHD. A formal diagnosis and treatment plan can significantly improve quality of life.',
        actionRequired: true
      };
    } else if (symptomaticScore >= 2) {
      return {
        level: 'Moderate Likelihood',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: Info,
        description: 'Your responses suggest some symptoms that may be consistent with ADHD.',
        recommendation: 'Consider discussing these symptoms with a healthcare provider. They can help determine if further evaluation is needed and discuss strategies to manage your symptoms.',
        actionRequired: true
      };
    } else {
      return {
        level: 'Low Likelihood',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: CheckCircle,
        description: 'Your responses do not suggest significant ADHD symptoms at this time.',
        recommendation: 'While your current screening does not indicate ADHD, if you continue to experience difficulties with attention, organization, or impulse control, consider speaking with a healthcare provider.',
        actionRequired: false
      };
    }
  };

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [questions[currentQuestion].id]: value });
    
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    } else {
      // Calculate and show results
      const symptomaticScore = calculateScore();
      setScore(symptomaticScore);
      setTimeout(() => {
        setShowResults(true);
      }, 300);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setScore(0);
  };

  const handleBookAppointment = () => {
    navigate('/booking/find-provider');
  };

  const handleDownloadResults = () => {
    // Generate simple text report
    const interpretation = getInterpretation(score);
    const date = new Date().toLocaleDateString();
    
    let report = `ADHD SCREENING RESULTS\n`;
    report += `Parivarthan Mental Health Platform\n`;
    report += `Date: ${date}\n\n`;
    report += `Adult ADHD Self-Report Scale (ASRS-v1.1) Screener\n\n`;
    report += `Symptomatic Responses: ${score} out of 6\n`;
    report += `Result: ${interpretation.level}\n\n`;
    report += `Interpretation:\n${interpretation.description}\n\n`;
    report += `Recommendation:\n${interpretation.recommendation}\n\n`;
    report += `IMPORTANT DISCLAIMER:\n`;
    report += `This screening is not a diagnosis. Only a qualified healthcare professional can diagnose ADHD.\n`;
    report += `Please consult with a psychiatrist or psychologist for a comprehensive evaluation.\n`;
    
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `adhd-screening-results-${date}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Scroll to top on question change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentQuestion, showResults]);

  // Results View
  if (showResults) {
    const interpretation = getInterpretation(score);
    const ResultIcon = interpretation.icon;

    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white py-6 sm:py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className={`w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full ${interpretation.bgColor} flex items-center justify-center`}>
                <ResultIcon className={`w-8 h-8 sm:w-10 sm:h-10 ${interpretation.color}`} />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Your ADHD Screening Results
              </h1>
              <p className="text-gray-600">Adult ADHD Self-Report Scale (ASRS-v1.1)</p>
            </div>

            {/* Score Display */}
            <div className={`border-2 ${interpretation.borderColor} ${interpretation.bgColor} rounded-lg p-6 mb-6`}>
              <div className="text-center">
                <p className="text-sm uppercase tracking-wide text-gray-600 mb-2">Result</p>
                <p className={`text-3xl sm:text-4xl font-bold ${interpretation.color} mb-2`}>
                  {interpretation.level}
                </p>
                <p className="text-lg text-gray-700">
                  {score} out of 6 symptomatic responses
                </p>
              </div>
            </div>

            {/* Interpretation */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">What This Means</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {interpretation.description}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {interpretation.recommendation}
              </p>
            </div>

            {/* Understanding ADHD Section */}
            <div className="bg-purple-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-purple-600" />
                Understanding ADHD
              </h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start">
                  <Activity className="w-5 h-5 mr-2 mt-1 text-purple-600 flex-shrink-0" />
                  <p className="text-sm">
                    <strong>Inattention symptoms:</strong> Difficulty sustaining attention, easily distracted, trouble organizing tasks, forgetfulness in daily activities
                  </p>
                </div>
                <div className="flex items-start">
                  <Target className="w-5 h-5 mr-2 mt-1 text-purple-600 flex-shrink-0" />
                  <p className="text-sm">
                    <strong>Hyperactivity symptoms:</strong> Fidgeting, difficulty sitting still, feeling restless or "driven by a motor"
                  </p>
                </div>
                <div className="flex items-start">
                  <Clock className="w-5 h-5 mr-2 mt-1 text-purple-600 flex-shrink-0" />
                  <p className="text-sm">
                    <strong>Important note:</strong> ADHD symptoms must be present since childhood and cause significant impairment in multiple areas of life to meet diagnostic criteria
                  </p>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            {interpretation.actionRequired && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommended Next Steps</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <Calendar className="w-5 h-5 mr-2 mt-1 text-blue-600 flex-shrink-0" />
                    <p className="text-gray-700 text-sm">
                      Schedule a comprehensive evaluation with a psychiatrist or psychologist specializing in ADHD
                    </p>
                  </div>
                  <div className="flex items-start">
                    <Brain className="w-5 h-5 mr-2 mt-1 text-blue-600 flex-shrink-0" />
                    <p className="text-gray-700 text-sm">
                      Keep a journal of your symptoms, including when they occur and how they affect your daily life
                    </p>
                  </div>
                  <div className="flex items-start">
                    <Info className="w-5 h-5 mr-2 mt-1 text-blue-600 flex-shrink-0" />
                    <p className="text-gray-700 text-sm">
                      Gather information about your childhood behavior and academic performance if possible
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <button
                onClick={handleBookAppointment}
                className="flex items-center justify-center px-6 py-3 bg-serene-blue-500 text-white rounded-lg hover:bg-serene-blue-600 transition-colors font-medium shadow-md"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </button>
              <button
                onClick={handleDownloadResults}
                className="flex items-center justify-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium shadow-md"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Results
              </button>
            </div>

            {/* Crisis Resources */}
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <Phone className="w-6 h-6 mr-3 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Need Immediate Support?</h3>
                  <p className="text-gray-700 text-sm mb-2">
                    If you're experiencing a mental health crisis or need immediate support:
                  </p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-700">
                      <strong>National Crisis Helpline:</strong>{' '}
                      <a href="tel:+18884047470" className="text-red-600 hover:underline font-medium">
                        +1 (888) 404-7470
                      </a>
                    </p>
                    <p className="text-gray-700">
                      <strong>Emergency:</strong>{' '}
                      <a href="tel:112" className="text-red-600 hover:underline font-medium">
                        112
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-gray-600" />
                Important Disclaimer
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                This screening tool is based on the WHO Adult ADHD Self-Report Scale (ASRS-v1.1) and is designed 
                for educational and screening purposes only. It is <strong>not a diagnostic tool</strong> and cannot 
                replace a comprehensive clinical evaluation. Only a qualified healthcare professional (psychiatrist, 
                psychologist, or other licensed mental health provider) can diagnose ADHD after a thorough assessment. 
                If you have concerns about ADHD, please consult with a healthcare provider for proper evaluation and 
                guidance.
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleRestart}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Retake Screening
              </button>
              <button
                onClick={() => navigate('/self-help')}
                className="flex-1 px-6 py-3 border-2 border-serene-blue-500 text-serene-blue-600 rounded-lg hover:bg-serene-blue-50 transition-colors font-medium"
              >
                Explore More Tools
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Assessment View
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white py-6 sm:py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <button
              onClick={() => navigate('/self-help')}
              className="flex items-center text-serene-blue-600 hover:text-serene-blue-700 mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Self-Help Tools
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              ADHD Screening Assessment
            </h1>
            <p className="text-gray-600">
              Adult ADHD Self-Report Scale (ASRS-v1.1) Screener
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 sm:mb-8">
            <div className="flex items-start">
              <Info className="w-5 h-5 mr-3 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="mb-2">
                  This screening uses the WHO Adult ADHD Self-Report Scale, a validated tool for identifying 
                  symptoms consistent with adult ADHD. Please answer based on how you have felt and conducted 
                  yourself <strong>over the past 6 months</strong>.
                </p>
                <p className="text-xs text-gray-600">
                  Time to complete: approximately 2-3 minutes
                </p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6 sm:mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">
                Question {currentQuestion + 1} of {questions.length}
              </span>
              <span className="text-sm text-gray-600">
                {Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-calm-purple-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-start mb-4">
              <Brain className="w-6 h-6 mr-3 text-calm-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  {questions[currentQuestion].text}
                </h2>
                <p className="text-sm text-gray-600">
                  Select the option that best describes your experience over the past 6 months
                </p>
              </div>
            </div>
          </div>

          {/* Answer Options */}
          <div className="space-y-3 mb-6">
            {answerOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(option.value)}
                className={`w-full p-4 sm:p-5 rounded-lg border-2 border-gray-200 text-left transition-all ${option.color} hover:border-calm-purple-400 hover:shadow-md active:scale-[0.98]`}
                aria-label={`Select ${option.label}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 font-medium text-base sm:text-lg">
                    {option.label}
                  </span>
                  <span className="text-gray-400 text-sm">
                    {option.value === 0 && '○'}
                    {option.value === 1 && '◔'}
                    {option.value === 2 && '◑'}
                    {option.value === 3 && '◕'}
                    {option.value === 4 && '●'}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`px-4 sm:px-6 py-2 rounded-lg transition-colors ${
                currentQuestion === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              {currentQuestion + 1} / {questions.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ADHDScreening;